import {
  type CSSProperties,
  type KeyboardEvent,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { usePopover } from '../../ui/usePopover'

/** The links a flyout holds, in the order the keyboard walks them. */
const linksIn = (panel: HTMLElement | null) =>
  Array.from(panel?.querySelectorAll<HTMLElement>('a') ?? [])

/** What Tab reaches, in document order. */
const TABBABLE =
  'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

/** How long the pointer may be between the rail and its flyout. */
const FLYOUT_GRACE_MS = 150

/**
 * A section's pages beside the icon rail, opened by pointing at or focusing
 * its icon.
 *
 * Dismissal is `usePopover`'s — a click elsewhere, Escape, going somewhere —
 * like every other floating thing. The panel is drawn on the body, since the
 * sidebar scrolls and would clip it, and opens BESIDE its row rather than
 * below it, so it places itself level with that row, kept on screen, and
 * places itself again whenever the row can move.
 *
 * The keyboard walks into it with the arrow toward it, through it with up and
 * down, and back out with the arrow away or Escape. Tab leaves it where the
 * sidebar continues, since a panel at the end of the document would otherwise
 * hand focus to whatever follows the app.
 */
export function useFlyout(enabled: boolean) {
  const { open, setOpen, ref: rowRef, panelRef } = usePopover()
  const [style, setStyle] = useState<CSSProperties>()
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined)
  const focusFirst = useRef(false)
  /* Focus handed back to the icon must not reopen the flyout. */
  const returning = useRef(false)

  const trigger = () => rowRef.current?.querySelector<HTMLElement>('a')
  const holdsFocus = () => {
    const at = document.activeElement
    return (
      !!at &&
      (!!rowRef.current?.contains(at) || !!panelRef.current?.contains(at))
    )
  }

  const show = () => {
    clearTimeout(timer.current)
    if (enabled) setOpen(true)
  }
  const hide = () => {
    clearTimeout(timer.current)
    setOpen(false)
  }
  /* Back to the icon, closed, without the focus reopening it. */
  const toTrigger = () => {
    hide()
    returning.current = true
    trigger()?.focus()
  }
  const showOnFocus = () => {
    if (returning.current) {
      returning.current = false
      return
    }
    show()
  }
  /* The pointer leaving closes it after a grace, unless the keyboard is
     working inside it. */
  const hideSoon = () => {
    clearTimeout(timer.current)
    timer.current = setTimeout(() => {
      if (!holdsFocus()) setOpen(false)
    }, FLYOUT_GRACE_MS)
  }
  /* Focus leaving for somewhere that is neither the icon nor the panel. */
  const hideOnBlur = (e: { relatedTarget: EventTarget | null }) => {
    const to = e.relatedTarget
    if (
      to instanceof Node &&
      (rowRef.current?.contains(to) || panelRef.current?.contains(to))
    )
      return
    hideSoon()
  }

  const arrows = (el: Element) =>
    getComputedStyle(el).direction === 'rtl'
      ? { into: 'ArrowLeft', out: 'ArrowRight' }
      : { into: 'ArrowRight', out: 'ArrowLeft' }

  const onTriggerKey = (e: KeyboardEvent) => {
    if (!enabled) return
    if (e.key === arrows(e.currentTarget).into || e.key === 'ArrowDown') {
      e.preventDefault()
      const first = linksIn(panelRef.current)[0]
      if (open && first) first.focus()
      else {
        focusFirst.current = true
        setOpen(true)
      }
    } else if (e.key === 'Escape') {
      hide()
    }
  }
  const onPanelKey = (e: KeyboardEvent) => {
    const all = linksIn(panelRef.current)
    const focused = document.activeElement
    const at = focused instanceof HTMLElement ? all.indexOf(focused) : -1
    const last = all.length - 1
    const go = (i: number) => {
      e.preventDefault()
      all[i]?.focus()
    }
    if (e.key === 'ArrowDown') go(at < last ? at + 1 : 0)
    else if (e.key === 'ArrowUp') go(at > 0 ? at - 1 : last)
    else if (e.key === 'Home') go(0)
    else if (e.key === 'End') go(last)
    else if (e.key === 'Escape' || e.key === arrows(e.currentTarget).out) {
      e.preventDefault()
      toTrigger()
    } else if (e.key === 'Tab' && e.shiftKey && at <= 0) {
      e.preventDefault()
      toTrigger()
    } else if (e.key === 'Tab' && !e.shiftKey && at === last) {
      /* On to what follows the icon in the page, skipping the panel itself. */
      const from = trigger()
      const order = Array.from(
        document.querySelectorAll<HTMLElement>(TABBABLE),
      ).filter((el) => !panelRef.current?.contains(el))
      const next = from ? order[order.indexOf(from) + 1] : undefined
      if (next) {
        e.preventDefault()
        hide()
        next.focus()
      }
    }
  }

  useLayoutEffect(() => {
    if (!open) return
    const place = () => {
      const row = rowRef.current
      const panel = panelRef.current
      if (!row || !panel) return
      const box = row.getBoundingClientRect()
      const rtl = getComputedStyle(row).direction === 'rtl'
      const top = Math.max(
        0,
        Math.min(box.top, window.innerHeight - panel.offsetHeight),
      )
      setStyle(
        rtl
          ? { top, right: window.innerWidth - box.left }
          : { top, left: box.right },
      )
    }
    place()
    if (focusFirst.current) {
      focusFirst.current = false
      linksIn(panelRef.current)[0]?.focus()
    }
    window.addEventListener('scroll', place, true)
    window.addEventListener('resize', place)
    return () => {
      window.removeEventListener('scroll', place, true)
      window.removeEventListener('resize', place)
    }
  }, [open, rowRef, panelRef])

  useLayoutEffect(() => () => clearTimeout(timer.current), [])
  useLayoutEffect(() => {
    if (!enabled) setOpen(false)
  }, [enabled, setOpen])

  return {
    open: enabled && open,
    style,
    rowRef,
    panelRef,
    show,
    hide,
    /* A page picked from the flyout: it closes, and the keyboard is back on
       the icon rather than on a link that is gone. */
    leave: toTrigger,
    hideSoon,
    hideOnBlur,
    showOnFocus,
    onTriggerKey,
    onPanelKey,
  }
}
